/**
 * Schema-driven instance setup — API client.
 */

import api from '@/services/api';
import type { InstanceSetupSchema, SetupFieldSchema } from '@/types/aiStudioAgent';
import { mapStatus, type SetupStatusRow } from '@/services/aiStudioInstanceSetupService';

const STUDIO_BASE = '/ai-studio';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

interface SetupFieldSchemaRow {
  key: string;
  label: string;
  description: string;
  type: SetupFieldSchema['type'];
  collectAt?: SetupFieldSchema['collectAt'];
  collect_at?: SetupFieldSchema['collectAt'];
  required: boolean;
  enumValues?: string[];
  enum_values?: string[];
  source: SetupFieldSchema['source'];
}

function mapField(row: SetupFieldSchemaRow): SetupFieldSchema {
  return {
    key: row.key,
    label: row.label,
    description: row.description,
    type: row.type,
    collectAt: row.collectAt ?? row.collect_at ?? 'provision',
    required: row.required,
    enumValues: row.enumValues ?? row.enum_values,
    source: row.source,
  };
}

export async function getInstanceSetupSchema(instanceId: string): Promise<InstanceSetupSchema> {
  const response = await api.get<
    ApiEnvelope<{
      instance_id: string;
      agent_id: string;
      agent_code: string;
      agent_version_id: string;
      version_number: number;
      industry_pack_id: string | null;
      fields: SetupFieldSchemaRow[];
      provision_fields: SetupFieldSchemaRow[];
    }>
  >(`${STUDIO_BASE}/instances/${instanceId}/setup-schema`);

  const row = response.data.data;
  return {
    instanceId: row.instance_id,
    agentId: row.agent_id,
    agentCode: row.agent_code,
    agentVersionId: row.agent_version_id,
    versionNumber: row.version_number,
    industryPackId: row.industry_pack_id,
    fields: (row.fields ?? []).map(mapField),
    provisionFields: (row.provision_fields ?? []).map(mapField),
  };
}

export { mapStatus, type SetupStatusRow };
